import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { normalizeError } from "@/lib/normalizeError";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboarding } from "@/hooks/useOnboarding";
import { SavedIdeas } from "@/components/SavedIdeas";
import { YourWorkspaces } from "@/components/YourWorkspaces";
import { ResearchPanel } from "@/components/dashboard/ResearchPanel";
import { IdeaList } from "@/components/IdeaList";
import { Button } from "@/components/ui/button";
import { GuidedOnboarding } from "@/components/onboarding/GuidedOnboarding";

interface Idea {
  id: string;
  title: string;
  description: string | null;
  source_url: string | null;
  tags: string[];
  niche: string;
  problem: string;
  solution: string;
  created_at: string;
  updated_at: string;
  score: number;
  ai_feedback: {
    strengths: string;
    weaknesses: string;
    suggestions: string;
  } | null;
}

const Dashboard = () => {
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { onboardingCompleted, loading: onboardingLoading, completeOnboarding } = useOnboarding();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Show onboarding for first-time users
  useEffect(() => {
    if (!onboardingLoading && !onboardingCompleted && user) {
      setShowOnboarding(true);
    }
  }, [onboardingCompleted, onboardingLoading, user]);

  const { data: ideasData, isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: ['dashboard-ideas', user?.id, currentPage],
    queryFn: async () => {
      if (!user) return { ideas: [], total: 0 };

      console.time('fetchDashboardIdeas');
      const from = (currentPage - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error: fetchError, count } = await supabase
        .from("ideas")
        .select("id, title, tagline, description, summary, tags, niche, score, created_at, updated_at, is_public", { count: 'exact' })
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (fetchError) throw fetchError;
      
      console.timeEnd('fetchDashboardIdeas');
      console.log(`[Performance] Loaded ${data?.length || 0} ideas in dashboard`);
      
      return { ideas: (data || []) as unknown as Idea[], total: count || 0 };
    },
    enabled: !!user,
  });

  const ideas = ideasData?.ideas || [];
  const totalIdeas = ideasData?.total || 0;
  const totalPages = Math.ceil(totalIdeas / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, totalIdeas);

  useEffect(() => {
    if (queryError) {
      const e = normalizeError(queryError);
      toast({
        title: "Error",
        description: e.message,
        variant: "destructive",
      });
    }
  }, [queryError, toast]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/signin");
    }
  }, [user, authLoading, navigate]);

  // Don't render if still loading auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect happens in useEffect, show loading while redirecting
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* DISABLED: GuidedOnboarding - temporarily removed to debug click issues */}
      {/* <GuidedOnboarding 
        open={showOnboarding} 
        onComplete={() => {
          setShowOnboarding(false);
          completeOnboarding();
        }} 
      /> */}
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Welcome back, {user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Founder'}!
            </h1>
            <p className="text-muted-foreground mt-2">
              Track your journey from discovery to shipped product
            </p>
          </div>

          {/* Saved Ideas Section */}
          <section>
            <SavedIdeas />
          </section>

          {/* Your Workspaces Section */}
          <section>
            <YourWorkspaces onWorkspaceSelect={(ideaId) => {
              window.location.href = `/build/${ideaId}`;
            }} />
          </section>

          {/* Research Panel Section */}
          <section>
            <ResearchPanel />
          </section>

          {/* Your Ideas Section */}
          <section className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold">Your Ideas</h2>
              <p className="text-muted-foreground text-sm mt-1">
                All your saved startup ideas in one place
              </p>
            </div>
            
            <IdeaList
              ideas={ideas}
              loading={loading}
              onIdeaUpdated={() => refetch()}
              onIdeaDeleted={() => refetch()}
              onIdeaSelect={setSelectedIdeaId}
              selectedIdeaId={selectedIdeaId}
              userId={user?.id || ""}
            />

            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages} ({totalIdeas} {totalIdeas === 1 ? 'idea' : 'ideas'})
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
